import {
  pxTovW,
  pxToRem,
  IStyles,
  QuestionContainerWithSolution,
  deserify,
  getTeacherSubjectName,
  useCommonServiceClientContext,
  theme,
  cloneObject,
  ActionsPopup,
  QuestionSourceTags,
  Loader,
} from '@geneo2-web/shared-ui';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import React, { Dispatch, SetStateAction, useCallback, useState } from 'react';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../reduxStore/reduxHooks';
import { Question } from '@protos/content_management/content.db_pb';
import {
  MarkingSchemeEnum,
  SectionQuestionMetaData,
  TaskCreationStatusEnum,
  TaskInfo,
} from '@protos/learning_management/lms.db_pb';
import { AssessmentTask } from '@protos/learning_management/lms.assessment.common.apis_pb';
import { questionHasImage } from '../../../QuestionPaperGenerator/ReviewQuestionPaper/utils';
import { InteractiveButtons } from '../../../QuestionPaperGenerator/ReviewQuestionPaper/InteractiveButtons';
import { AssessmentCreationEnum } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  setSelectedQuestionIndexForAlternateQuestion, // selected_question_index_for_alternate : number | undefined
  setSelectedQuestionTypeForAlternateQuestion, // selected_question_type_for_alternate : Question_QuestionEnum | undefined
  setFAContent, // selected_section_index_for_alternate : number | undefined
} from '../../../reducer/assessmentFlow.slice';
import { setToastInfo } from 'apps/teacher/src/pages/Home/reducer/homeDashboard.slice';
import { ConnectError } from '@bufbuild/connect';
import { FA_QUESTION_BANK } from '../../../../../routeHandling/RoutesNomenclature';
import { Timestamp } from '@bufbuild/protobuf';
import { getUniqueQuestionIdsFromQpgContent } from '../../../utils/QPGv2.utils';
import {
  useAddOrReplaceFAQuestionMultiSubject,
  useDeleteFAQuestionMultiSubject,
  useShowMediaClickFAMultiSubject,
} from '../../FAv2.utils';

const styles: IStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(10), md: pxTovW(15) },
    backgroundColor: '#ffffff',
    borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
    border: '1px solid #E0E2E4',
    paddingX: { xs: pxToRem(20), md: pxTovW(20) },
    paddingY: { xs: pxToRem(16), md: pxTovW(30) },
    marginY: { xs: pxToRem(10), md: pxTovW(40) },
  },
  subjectSectionDetails: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  ellipsisText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
  },
  accordion: {
    border: '1px solid #E0E2E4',
    borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
    overflow: 'hidden',
    // remove default MUI line
    '&::before': {
      display: 'none',
    },
    // remove double borders between items
    '&:not(:last-of-type)': {
      borderBottom: 'none',
    },
    // TOP radius
    '&:first-of-type': {
      borderTopLeftRadius: { xs: pxToRem(15), md: pxTovW(15) },
      borderTopRightRadius: { xs: pxToRem(15), md: pxTovW(15) },
    },
    // BOTTOM radius ⭐ FIX
    '&:last-of-type': {
      borderBottomLeftRadius: { xs: pxToRem(15), md: pxTovW(15) },
      borderBottomRightRadius: { xs: pxToRem(15), md: pxTovW(15) },
    },
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      margin: 0,
      padding: { xs: pxToRem(10), md: pxTovW(20) },
    },
  },
  subSectionDetails: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(5), md: pxTovW(30) },
    marginTop: { xs: pxToRem(10), md: pxTovW(10) },
  },
  detailsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(5), md: pxTovW(5) },
  },
  addMoreButton: {
    background: '#C1F8DA',
    display: 'flex',
    borderRadius: { xs: pxToRem(6), md: pxTovW(10) },
    boxSizing: 'border-box',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid #0AA34F',
    paddingX: { xs: pxToRem(10), md: pxTovW(10) },
    paddingY: { xs: pxToRem(10), md: pxTovW(10) },
    gap: { xs: pxToRem(5), md: pxTovW(16) },
    // height: { xs: pxToRem(28), md: pxTovW(57) },
  },
  accordionDetails: {
    paddingY: { xs: pxToRem(10), md: pxTovW(25) },
    paddingX: { xs: pxToRem(10), md: pxTovW(25) },
    borderTop: '2px solid #E0E2E4',
  },
  instructionText: {
    fontFamily: 'Poppins',
    marginTop: { xs: pxToRem(15), md: pxTovW(15) },
    marginBottom: { xs: pxToRem(40), md: pxTovW(40) },
    fontSize: { xs: pxToRem(12), md: 'unset' },
  },
  subSectionHeading: {
    borderRadius: { xs: pxToRem(28), md: pxTovW(28) },
    backgroundColor: '#E1F2FF',
    paddingX: { xs: pxToRem(25), md: pxTovW(25) },
    paddingY: { xs: pxToRem(10), md: pxTovW(10) },
    width: 'max-content',
    marginX: 'auto',
    marginY: { xs: pxToRem(15), md: pxTovW(30) },
  },
};

interface IProps {
  setUpdatingAssessment: Dispatch<SetStateAction<boolean>>;
  handleSave: (props: {
    createAssessment?: boolean;
    editAssessmentCreationFlow?: 'chapter' | 'bloom';
  }) => Promise<void>;
}

export const FAReviewSection = (props: IProps) => {
  const { setUpdatingAssessment, handleSave } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler } =
    useCommonServiceClientContext();
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const {
    fa_content,
    fa_selected_section_number,
    fa_selected_chapters,
    fa_settings_config,
    fa_created_section_test,
  } = deserify(useAppSelector((state) => state.assessment));

  const [loading, setLoading] = useState<'loading' | 'success' | 'error'>(
    'success'
  );
  const [replacedQuestionIds, setReplacedQuestionIds] = useState<string[]>([]);
  const [deleteQuestionPopupState, setDeleteQuestionPopupState] = useState<
    | {
        sectionIndex: number;
        subSectionIndex: number;
        questionIndex?: number;
        altIdx?: number;
      }
    | undefined
  >(undefined);

  // First accordion open by default; only one open at a time
  const [expandedAccordion, setExpandedAccordion] = useState<number>(0);

  const faAssessmentQuestions =
    fa_content?.assessmentContent?.map((ques) => new Question(ques)) || [];
  const faQuestionsSequenceInfo = new TaskInfo(
    fa_content?.questionsSequenceInfo
  );
  const taskQuestionInfo = faQuestionsSequenceInfo.taskQuestionInfo || [];
  const faQuestionTypeMarks = fa_content?.questionTypeAndMarks || [];
  const faAssessmentTask = new AssessmentTask(fa_content?.assessment);
  const taskCreationStatus = faAssessmentTask.taskCreationStatus;
  const assessmentContent = fa_content?.assessmentContent || [];

  const sectionToShow = fa_content?.multiSubjectSections.find(
    (section) => section.sectionNo === fa_selected_section_number
  );
  const isJeeMarkingScheme =
    sectionToShow?.markingSchemeType === MarkingSchemeEnum.MARKING_SCHEME_JEE;

  const multiSubjectSubSections = sectionToShow?.multiSubjectSubSections || [];

  // --------------------------------------------------------------

  const getFormattedQuestionFromId = (id: string) => {
    const ques = assessmentContent?.find((ques) => ques.questionId == id);
    if (ques) {
      return ques;
    }
    return undefined;
  };

  // --------------------------------------------------------------

  const questionsList = (includeAlternateQuestions = false) =>
    multiSubjectSubSections
      .map((subSection) =>
        subSection.subSectionMetaData.map((quesList) => [
          getFormattedQuestionFromId(quesList.questionId),
          ...(includeAlternateQuestions
            ? quesList.alternateQuestions.map((altQuestion) =>
                getFormattedQuestionFromId(altQuestion.questionId)
              )
            : []),
        ])
      )
      .flat()
      .flat()
      .filter((val) => val !== undefined) as Question[];

  const questionsListWithAlternateQuestions = questionsList(true);
  // const questionModuleMap = fa_content?.questionModuleMap || [];
  const questionModuleMap =
    fa_content?.questionsSequenceInfo?.questionModuleMap || [];
  const addOrReplaceQuestion = useAddOrReplaceFAQuestionMultiSubject();
  const deleteQuestion = useDeleteFAQuestionMultiSubject();
  const showMediaClick = useShowMediaClickFAMultiSubject();
  const hasAiGeneratedQuestions =
    fa_content?.assessment?.isAiAssessment ||
    multiSubjectSubSections.some((subSection) =>
      subSection.subSectionMetaData.some(
        (ques) =>
          ques.isAiGenerated ||
          ques.alternateQuestions.some((alt) => alt.isAiGenerated)
      )
    );

  // --------------------------------------------------------------

  const handleMarksChangeForQuestion = useCallback(
    (params: {
      effectiveSectionIndex: number;
      subSectionIndex: number;
      questionId: string;
      newMarks: number;
    }) => {
      const { effectiveSectionIndex, subSectionIndex, questionId, newMarks } =
        params;

      if (!fa_content?.multiSubjectSections?.length) return;
      if (!questionId) return;

      const updated = cloneObject(fa_content);
      const sectionCopy =
        updated?.multiSubjectSections?.[effectiveSectionIndex];
      const subSectionCopy =
        sectionCopy?.multiSubjectSubSections?.[subSectionIndex];
      const meta = subSectionCopy?.subSectionMetaData;

      if (!sectionCopy || !subSectionCopy || !meta?.length) return;

      const qIdx = meta.findIndex((q) => q.questionId === questionId);
      if (qIdx < 0) return;

      meta[qIdx] = new SectionQuestionMetaData({
        ...meta[qIdx],
        maxMarks: newMarks,
      });

      subSectionCopy.subSectionMetaData = meta;
      sectionCopy.multiSubjectSubSections[subSectionIndex] = subSectionCopy;
      updated.multiSubjectSections[effectiveSectionIndex] = sectionCopy;
      dispatch(setFAContent(updated));
    },
    [dispatch, fa_content]
  );

  // --------------------------------------------------------------

  // Handler for drag-and-drop
  const onDragEnd = (
    result: any,
    sectionIndex?: number,
    subSectionIndex?: number
  ) => {
    if (!result.destination) return;
    const { source, destination, type } = result;

    // Level 1: Reorder questions within a subSection (update fa_created_section_test)
    if (
      type === 'MAIN_BOX' &&
      typeof sectionIndex === 'number' &&
      typeof subSectionIndex === 'number' &&
      fa_content?.multiSubjectSections
    ) {
      const section = fa_content.multiSubjectSections[sectionIndex];
      const subSection = section?.multiSubjectSubSections?.[subSectionIndex];
      const metaData = subSection?.subSectionMetaData;
      if (!section || !subSection || !metaData?.length) return;

      const updated = cloneObject(fa_content);
      const sectionCopy = updated?.multiSubjectSections[sectionIndex];
      const subSectionCopy =
        sectionCopy?.multiSubjectSubSections?.[subSectionIndex];
      if (!subSectionCopy?.subSectionMetaData) return;

      const list = [...subSectionCopy.subSectionMetaData].sort(
        (a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0)
      );
      const [removed] = list.splice(source.index, 1);
      list.splice(destination.index, 0, removed);
      list.forEach((q, idx) => {
        if (q) q.sequence = idx;
      });
      subSectionCopy.subSectionMetaData = list;
      sectionCopy.multiSubjectSubSections[subSectionIndex] = subSectionCopy;
      updated.multiSubjectSections[sectionIndex] = sectionCopy;
      dispatch(setFAContent(updated));
      // dispatch(setFACreatedSectionTest(updated));
      return;
    }

    // Level 2: Reorder alternates within a question (update fa_created_section_test)
    if (
      type.startsWith('ALTERNATE_QUESTION') &&
      fa_content?.multiSubjectSections
    ) {
      // type: ALTERNATE_QUESTION-<sectionIndex>-<subSectionIndex>-<questionIndex>
      const parts = type.split('-');
      const secIdxNum = Number(parts[1]);
      const subSecIdxNum = Number(parts[2]);
      const quesIdxNum = Number(parts[3]);
      if (isNaN(secIdxNum) || isNaN(subSecIdxNum) || isNaN(quesIdxNum)) return;

      const section = fa_content?.multiSubjectSections[secIdxNum];
      const subSection = section?.multiSubjectSubSections?.[subSecIdxNum];
      const metaData = subSection?.subSectionMetaData;
      const quesMeta = metaData?.[quesIdxNum] as
        | SectionQuestionMetaData
        | undefined;
      if (!section || !subSection || !metaData || !quesMeta) return;

      const updated = cloneObject(fa_content);
      const sectionCopy = updated?.multiSubjectSections[secIdxNum];
      const subSectionCopy =
        sectionCopy?.multiSubjectSubSections?.[subSecIdxNum];
      const listCopy = subSectionCopy?.subSectionMetaData;
      const quesMetaCopy = listCopy?.[quesIdxNum] as
        | SectionQuestionMetaData
        | undefined;
      if (!listCopy || !quesMetaCopy) return;

      const outerSequence = quesMetaCopy.sequence ?? 0;
      const allQuestions = [
        cloneObject(quesMetaCopy),
        ...(quesMetaCopy.alternateQuestions ?? [])
          .sort((a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0))
          .map((val) => {
            const v = cloneObject(val);
            v.alternateQuestions = [];
            return v;
          }),
      ];
      const [removedAlt] = allQuestions.splice(source.index, 1);
      allQuestions.splice(destination.index, 0, removedAlt);
      const newMain = allQuestions[0];
      const newAlternates = allQuestions
        .slice(1)
        .map(
          (val, i) => new SectionQuestionMetaData({ ...val, sequence: i + 1 })
        );
      listCopy[quesIdxNum] = new SectionQuestionMetaData({
        ...newMain,
        alternateQuestions: newAlternates,
        sequence: outerSequence,
      });
      // dispatch(setFACreatedSectionTest(updated));
      updated.multiSubjectSections[secIdxNum] = sectionCopy;
      sectionCopy.multiSubjectSubSections[subSecIdxNum] = subSectionCopy;

      dispatch(setFAContent(updated));
    }
  };

  // --------------------------------------------------------------

  const getMarksForQuestionId = (questionId: string): number => {
    const ques = faAssessmentQuestions.find((q) => q.questionId == questionId);
    const taskInfo = taskQuestionInfo.find(
      (info) => info.questionId == questionId
    );
    const quesType = ques?.questionType;
    const foundQuesType = faQuestionTypeMarks.find(
      (qType) => qType.questionType == quesType
    );
    if (foundQuesType) return foundQuesType.marks ?? 0;
    if (typeof taskInfo?.maxMarks === 'number') return taskInfo.maxMarks;
    const existing =
      ques?.question?.model?.value?.commonQuestionContent?.marks?.[0];
    if (typeof existing === 'number') return existing;
    return 0;
  };

  // --------------------------------------------------------------

  const handleReplaceClick = async (props: {
    sectionIndex: number;
    subSectionIndex: number;
    index: number;
    altIdx: number;
    currentQuestion?: Question;
  }) => {
    const { sectionIndex, subSectionIndex, index, altIdx, currentQuestion } =
      props;

    if (!currentQuestion) return;
    try {
      setUpdatingAssessment(true);

      // Get bloom level and difficulty level from current question
      const questionBloomLevels = currentQuestion.questionMeta?.bloomLevel
        ? [currentQuestion.questionMeta?.bloomLevel]
        : undefined;
      const requiredDifficultyTypes = currentQuestion.questionMeta
        ?.difficultyLevel
        ? [currentQuestion.questionMeta.difficultyLevel]
        : [];

      // Get all module IDs for fallback
      const allModuleIds =
        sectionToShow?.chapterInfo?.chapterTopicsInfo
          ?.flatMap((chapter) => [
            chapter.chapterId,
            ...chapter.topicInfo.map((topic) => topic.topicId),
          ])
          ?.sort((a, b) => a - b) || [];

      // Get the moduleId from questionModuleMap
      const questionMapEntry = questionModuleMap.find(
        (q) => q.questionId === currentQuestion.questionId
      );
      let moduleQuestions;

      const ignoreIds = new Set<string>([
        ...getUniqueQuestionIdsFromQpgContent({
          qpgContent: fa_content,
        }),
        ...questionsListWithAlternateQuestions.map(
          (ques) => ques.questionId || ''
        ),
        ...replacedQuestionIds,
      ]);

      setLoading('loading');

      // Attempt 1: specific moduleId + bloom level of the question
      if (
        questionMapEntry &&
        (questionMapEntry.topicId || questionMapEntry.chapterId)
      ) {
        const specificModuleId =
          questionMapEntry.topicId ?? questionMapEntry.chapterId;
        if (specificModuleId) {
          moduleQuestions =
            await LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler.getModuleQuestionsWithFilters(
              {
                teacherId: user_info?.teacherProfileId,
                subjectId:
                  fa_content?.multiSubjectSections[sectionIndex].subjectId,
                sectionId: fa_content?.assessment?.sectionId,
                moduleIds: [specificModuleId],
                assessmentCreationType:
                  AssessmentCreationEnum.ASSESSMENT_CREATION_FORMATIVE,
                noOfQuestions: 1,
                requiredQuestionTypes: [currentQuestion.questionType],
                requiredDifficultyTypes: requiredDifficultyTypes,
                bloomLevels: questionBloomLevels,
                qIdsToBeIgnored: Array.from(ignoreIds),
                currentQuestionId: currentQuestion.questionId,
                modeOfConduction: fa_content?.assessment?.modeOfConduction,
              }
            );

          // If no questions found, reset to trigger fallback
          if (
            !moduleQuestions.data?.questions ||
            moduleQuestions.data.questions.length === 0
          ) {
            moduleQuestions = undefined;
          }
        }
      }

      // Fallback 1: bloom level of that question with all module ids
      if (
        (!moduleQuestions || !moduleQuestions.data?.questions?.length) &&
        questionBloomLevels
      ) {
        moduleQuestions =
          await LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler.getModuleQuestionsWithFilters(
            {
              teacherId: user_info?.teacherProfileId,
              subjectId:
                fa_content?.multiSubjectSections[sectionIndex].subjectId,
              sectionId: fa_content?.assessment?.sectionId,
              moduleIds: allModuleIds,
              assessmentCreationType:
                AssessmentCreationEnum.ASSESSMENT_CREATION_FORMATIVE,
              noOfQuestions: 1,
              requiredQuestionTypes: [currentQuestion.questionType],
              requiredDifficultyTypes: requiredDifficultyTypes,
              bloomLevels: questionBloomLevels,
              qIdsToBeIgnored: Array.from(ignoreIds),
              currentQuestionId: currentQuestion.questionId,
              modeOfConduction: fa_content?.assessment?.modeOfConduction,
            }
          );

        // If no questions found, reset to trigger fallback 2
        if (
          !moduleQuestions.data?.questions ||
          moduleQuestions.data.questions.length === 0
        ) {
          moduleQuestions = undefined;
        }
      }

      // Fallback 2: no bloom level + all module ids
      if (!moduleQuestions || !moduleQuestions.data?.questions?.length) {
        moduleQuestions =
          await LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler.getModuleQuestionsWithFilters(
            {
              teacherId: user_info?.teacherProfileId,
              subjectId:
                fa_content?.multiSubjectSections[sectionIndex].subjectId,
              sectionId: fa_content?.assessment?.sectionId,
              moduleIds: allModuleIds,
              assessmentCreationType:
                AssessmentCreationEnum.ASSESSMENT_CREATION_FORMATIVE,
              noOfQuestions: 1,
              requiredQuestionTypes: [currentQuestion.questionType],
              requiredDifficultyTypes: requiredDifficultyTypes,
              qIdsToBeIgnored: Array.from(ignoreIds),
              currentQuestionId: currentQuestion.questionId,
              modeOfConduction: fa_content?.assessment?.modeOfConduction,
            }
          );
      }

      setLoading('success');

      if (moduleQuestions && moduleQuestions.data?.questions?.length) {
        const newQuestion = moduleQuestions.data.questions[0];
        const questionToReplaceWith = cloneObject(newQuestion);
        // if (
        //   isJeeMarkingScheme &&
        //   questionToReplaceWith?.question?.model?.value?.commonQuestionContent
        //     ?.marks
        // ) {
        //   questionToReplaceWith.question.model.value.commonQuestionContent.marks[0] = 4;
        // }
        const questionsSequenceInfo = new TaskInfo(
          fa_content?.questionsSequenceInfo
        );
        const marksOverride =
          sectionToShow?.markingSchemeType ===
          MarkingSchemeEnum.MARKING_SCHEME_JEE
            ? 4
            : sectionToShow?.markingSchemeType ===
              MarkingSchemeEnum.MARKING_SCHEME_CUSTOM
            ? questionsSequenceInfo.marksPerQuestion || 0
            : undefined;

        if (
          marksOverride !== undefined &&
          questionToReplaceWith?.question?.model?.value?.commonQuestionContent
            ?.marks
        ) {
          questionToReplaceWith.question.model.value.commonQuestionContent.marks[0] =
            marksOverride;
        }
        if (
          currentQuestion.questionId &&
          !replacedQuestionIds.includes(currentQuestion.questionId)
        ) {
          setReplacedQuestionIds([
            ...replacedQuestionIds,
            currentQuestion.questionId,
          ]);
        }

        await addOrReplaceQuestion({
          question: questionToReplaceWith,
          subSectionIndex: subSectionIndex,
          questionIndex: index,
          altIdx: altIdx,
          moduleMapArray: moduleQuestions.data?.questionModuleMap,
        });

        dispatch(
          setToastInfo({
            label: 'Question replaced successfully',
            variant: 'info',
            open: true,
          })
        );
      } else {
        dispatch(
          setToastInfo({
            label: 'No questions found to replace',
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (err) {
      setLoading('error');
      let message = 'Failed to replace question';
      if (err instanceof ConnectError) {
        message = err.rawMessage?.replace(/.*\]\s*/, '').trim();
      }
      console.log(err);
      dispatch(
        setToastInfo({
          label: message,
          variant: 'error',
          open: true,
        })
      );
    } finally {
      setLoading('success');
      setUpdatingAssessment(false);
    }
  };

  // --------------------------------------------------------------

  const handleShowMediaClick = async (index: number, altIdx: number) => {
    showMediaClick({
      subSectionIndex: 0,
      questionIndex: index,
      altIdx: altIdx,
    });
    handleSave({});
  };

  // --------------------------------------------------------------

  const getSubjectName = (subjectId: number) =>
    getTeacherSubjectName(subjectId, user_info?.teachClassSubjects) ?? '';

  // --------------------------------------------------------------

  const subsectionTotalMarks = (subSection: {
    subSectionMetaData?: Array<{ questionId: string }>;
  }): number => {
    const meta = subSection?.subSectionMetaData;
    if (!meta?.length) return 0;
    return meta.reduce(
      (sum, entry) => sum + getMarksForQuestionId(entry.questionId),
      0
    );
  };

  const effectiveSectionIndex =
    fa_content?.multiSubjectSections.findIndex(
      (section) => section.sectionNo === fa_selected_section_number
    ) ?? 0;

  // --------------------------------------------------------------

  if (loading === 'loading') return <Loader />;

  return (
    <Box sx={styles.root}>
      {sectionToShow ? (
        <DragDropContext
          key={fa_selected_section_number + sectionToShow.sectionName}
          onDragEnd={(result) => {
            if (result.type === 'MAIN_BOX') {
              const match =
                result.source.droppableId.match(/section-(\d+)-(\d+)/);
              if (match) {
                onDragEnd(
                  result,
                  parseInt(match[1], 10),
                  parseInt(match[2], 10)
                );
              }
            } else if (result.type.startsWith('ALTERNATE_QUESTION')) {
              onDragEnd(result);
            }
          }}
        >
          <Box sx={styles.subjectSectionDetails}>
            {sectionToShow.sectionTitle ? (
              <Typography
                variant="h2"
                color="primary.main"
                sx={styles.ellipsisText}
              >
                Section {effectiveSectionIndex + 1} -{' '}
                {getSubjectName(sectionToShow.subjectId)} -{' '}
                {sectionToShow.sectionTitle}
              </Typography>
            ) : (
              <Typography
                variant="h2"
                color="primary.main"
                sx={styles.ellipsisText}
              >
                Section {effectiveSectionIndex + 1} -{' '}
                {getSubjectName(sectionToShow.subjectId)}
              </Typography>
            )}
          </Box>

          {sectionToShow.multiSubjectSubSections.map(
            (subSection, subSectionIndex) => (
              <Droppable
                droppableId={`section-${effectiveSectionIndex}-${subSectionIndex}`}
                type="MAIN_BOX"
              >
                {(provided) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: { md: pxTovW(40), xs: pxToRem(15) },
                    }}
                  >
                    {[...(subSection.subSectionMetaData ?? [])]
                      .sort((a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0))
                      .map((ques, index) => {
                        const allQuestions = [
                          ques,
                          ...[...(ques.alternateQuestions ?? [])].sort(
                            (a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0)
                          ),
                        ];
                        const questions = allQuestions.map((ques) => {
                          const q = getFormattedQuestionFromId(ques.questionId);
                          const quesType = q?.questionType;
                          const subSectionMarks = quesType
                            ? subSection.questionTypeAndMarks.find(
                                (val) => val.questionType == quesType
                              )
                            : undefined;
                          if (q?.question?.model.value?.commonQuestionContent) {
                            if (ques.maxMarks) {
                              q.question.model.value.commonQuestionContent.marks =
                                [ques.maxMarks];
                            } else if (subSectionMarks) {
                              q.question.model.value.commonQuestionContent.marks =
                                [subSectionMarks.marks];
                            }
                          }
                          return q;
                        });
                        if (questions.length === 0) {
                          return null;
                        }

                        // Level 1 Draggable
                        return (
                          <Draggable
                            key={ques.questionId}
                            draggableId={ques.questionId}
                            index={index}
                            isDragDisabled={
                              taskCreationStatus ===
                              TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED
                            }
                          >
                            {(provided) => {
                              const firstQuestion = allQuestions[0];
                              const showMedia = firstQuestion.showMedia;
                              const questionData =
                                fa_content?.assessmentContent.find(
                                  (q) => q.questionId === ques.questionId
                                );
                              return (
                                <Box
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                >
                                  {questions.length === 1 ? (
                                    <QuestionContainerWithSolution
                                      customMarks={
                                        sectionToShow.markingSchemeType !==
                                          MarkingSchemeEnum.MARKING_SCHEME_JEE &&
                                        sectionToShow.markingSchemeType !==
                                          MarkingSchemeEnum.MARKING_SCHEME_CUSTOM
                                      }
                                      hideBloomLevel={
                                        sectionToShow.markingSchemeType ===
                                        MarkingSchemeEnum.MARKING_SCHEME_JEE
                                      }
                                      onMarksChange={(newMarks: number) => {
                                        if (!ques?.questionId) return;
                                        handleMarksChangeForQuestion({
                                          effectiveSectionIndex,
                                          subSectionIndex,
                                          questionId: ques.questionId,
                                          newMarks,
                                        });
                                      }}
                                      questionNumber={index + 1}
                                      hideImage={
                                        taskCreationStatus ===
                                          TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED &&
                                        !showMedia
                                      }
                                      headerLeftElement={
                                        <QuestionSourceTags
                                          question={questionData}
                                          teacherProfileId={
                                            user_info?.teacherProfileId
                                          }
                                          teacherName={
                                            user_info?.userName || ''
                                          }
                                        />
                                      }
                                      headerRightElement={
                                        <Box
                                          {...provided.dragHandleProps}
                                          sx={{ flex: 1 }}
                                        >
                                          <InteractiveButtons
                                            stackComponents={isMobile}
                                            showAIButton={
                                              questions[0]?.isAiGenerated
                                            }
                                            handleAIPrompt={(prompt) => {
                                              handleReplaceClick({
                                                sectionIndex:
                                                  effectiveSectionIndex,
                                                subSectionIndex:
                                                  subSectionIndex,
                                                index: index,
                                                altIdx: 0,
                                                currentQuestion: questions[0],
                                              });
                                            }}
                                            isIncludeImageChecked={showMedia}
                                            showIncludeImageButton={questionHasImage(
                                              questions[0]
                                            )}
                                            handleIncludeImageClick={() => {
                                              handleShowMediaClick(index, 0);
                                            }}
                                            // addAlternateQuestionsClick={() => {
                                            //   dispatch(
                                            //     setSelectedQuestionIndexForAlternateQuestion(
                                            //       index
                                            //     )
                                            //   );
                                            //   dispatch(
                                            //     setSelectedQuestionTypeForAlternateQuestion(
                                            //       questions[0]?.questionType
                                            //     )
                                            //   );
                                            //   navigate(FA_QUESTION_BANK, {
                                            //     replace: true,
                                            //     state: {
                                            //       from: location.pathname,
                                            //       redirectionPath:
                                            //         location.state
                                            //           ?.redirectionPath,
                                            //     },
                                            //   });
                                            // }}
                                            onDeleteClick={
                                              sectionToShow
                                                .multiSubjectSubSections[0]
                                                .subSectionMetaData.length === 1
                                                ? undefined
                                                : () => {
                                                    setDeleteQuestionPopupState(
                                                      {
                                                        sectionIndex:
                                                          effectiveSectionIndex,
                                                        subSectionIndex,
                                                        questionIndex: index,
                                                        altIdx: 0,
                                                      }
                                                    );
                                                  }
                                            }
                                            handleReplaceClick={() =>
                                              handleReplaceClick({
                                                sectionIndex:
                                                  effectiveSectionIndex,
                                                subSectionIndex:
                                                  subSectionIndex,
                                                index: index,
                                                altIdx: 0,
                                                currentQuestion: questions[0],
                                              })
                                            }
                                          />
                                        </Box>
                                      }
                                      question={questions[0]}
                                      showQuestionStats
                                      showAnswer
                                      imageResizeEnabled={true}
                                      key={'ques_' + index}
                                    />
                                  ) : (
                                    <Box
                                      sx={{
                                        border: `1px solid #007CDC`,
                                        borderRadius: {
                                          xs: pxToRem(20),
                                          md: pxTovW(20),
                                        },
                                        padding: {
                                          xs: `${pxTovW(14)} ${pxTovW(10)}`,
                                          md: `${pxTovW(18)} ${pxTovW(13)}`,
                                        },
                                        backgroundColor: '#F5F5FF',
                                        display: 'flex',
                                        flexDirection: 'column',
                                      }}
                                      key={'alternate_ques_' + index}
                                    >
                                      <Box
                                        sx={{ display: 'flex' }}
                                        {...provided.dragHandleProps}
                                      >
                                        {taskCreationStatus !==
                                        TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED ? (
                                          <Box
                                            sx={{
                                              ml: 'auto',
                                              pr: {
                                                xs: pxToRem(10),
                                                md: pxTovW(15),
                                              },
                                              pb: {
                                                xs: pxToRem(6),
                                                md: pxTovW(10),
                                              },
                                              pt: {
                                                xs: pxToRem(6),
                                                md: pxTovW(0),
                                              },
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: pxTovW(14),
                                            }}
                                          >
                                            {/* Blue outer box */}
                                            <InteractiveButtons
                                              // addAlternateQuestionsClick={() => {
                                              //   dispatch(
                                              //     setSelectedQuestionIndexForAlternateQuestion(
                                              //       index
                                              //     )
                                              //   );
                                              //   dispatch(
                                              //     setSelectedQuestionTypeForAlternateQuestion(
                                              //       questions[0]?.questionType
                                              //     )
                                              //   );
                                              //   navigate(FA_QUESTION_BANK, {
                                              //     replace: true,
                                              //     state: {
                                              //       from: location.pathname,
                                              //       redirectionPath:
                                              //         location.state
                                              //           ?.redirectionPath,
                                              //     },
                                              //   });
                                              // }}
                                              onDeleteClick={() => {
                                                sectionToShow
                                                  .multiSubjectSubSections[0]
                                                  .subSectionMetaData.length ===
                                                1
                                                  ? undefined
                                                  : setDeleteQuestionPopupState(
                                                      {
                                                        sectionIndex:
                                                          effectiveSectionIndex,
                                                        subSectionIndex,
                                                        questionIndex: index,
                                                      }
                                                    );
                                              }}
                                            />
                                          </Box>
                                        ) : (
                                          <Box {...provided.dragHandleProps} />
                                        )}
                                      </Box>
                                      {/* Level 2 DragDrop for main + alternates */}
                                      <Droppable
                                        droppableId={`alternates-${effectiveSectionIndex}-${subSectionIndex}-${index}`}
                                        type={`ALTERNATE_QUESTION-${effectiveSectionIndex}-${subSectionIndex}-${index}`}
                                        direction="vertical"
                                      >
                                        {(altProvided) => {
                                          return (
                                            <Box
                                              ref={altProvided.innerRef}
                                              {...altProvided.droppableProps}
                                              sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: {
                                                  md: pxTovW(20),
                                                  xs: pxToRem(10),
                                                },
                                              }}
                                            >
                                              {allQuestions.map(
                                                (ques, altIdx, arr) => {
                                                  const qid = ques.questionId;
                                                  const question =
                                                    questions.find(
                                                      (val) =>
                                                        val?.questionId == qid
                                                    );
                                                  const isAiGenerated =
                                                    question?.isAiGenerated;
                                                  const showMedia =
                                                    ques.showMedia;
                                                  return (
                                                    <React.Fragment key={qid}>
                                                      <Draggable
                                                        key={qid}
                                                        draggableId={`${qid}-alt`}
                                                        index={altIdx}
                                                        isDragDisabled={
                                                          taskCreationStatus ===
                                                          TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED
                                                        }
                                                      >
                                                        {(altDraggable) => (
                                                          <Box
                                                            ref={
                                                              altDraggable.innerRef
                                                            }
                                                            {...altDraggable.draggableProps}
                                                          >
                                                            <QuestionContainerWithSolution
                                                              questionNumber={
                                                                index + 1
                                                              }
                                                              hideImage={
                                                                taskCreationStatus ===
                                                                  TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED &&
                                                                !showMedia
                                                              }
                                                              headerLeftElement={
                                                                <QuestionSourceTags
                                                                  question={
                                                                    questionData
                                                                  }
                                                                  teacherProfileId={
                                                                    user_info?.teacherProfileId
                                                                  }
                                                                  teacherName={
                                                                    user_info?.userName ||
                                                                    ''
                                                                  }
                                                                />
                                                              }
                                                              headerRightElement={
                                                                taskCreationStatus !==
                                                                TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED ? (
                                                                  <Box
                                                                    {...altDraggable.dragHandleProps}
                                                                    sx={{
                                                                      flex: 1,
                                                                    }}
                                                                  >
                                                                    <InteractiveButtons
                                                                      stackComponents={
                                                                        isMobile
                                                                      }
                                                                      showAIButton={
                                                                        isAiGenerated
                                                                      }
                                                                      handleAIPrompt={(
                                                                        prompt
                                                                      ) => {
                                                                        handleReplaceClick(
                                                                          {
                                                                            sectionIndex:
                                                                              effectiveSectionIndex,
                                                                            subSectionIndex:
                                                                              subSectionIndex,
                                                                            index:
                                                                              index,
                                                                            altIdx:
                                                                              altIdx,
                                                                            currentQuestion:
                                                                              question,
                                                                          }
                                                                        );
                                                                      }}
                                                                      showIncludeImageButton={questionHasImage(
                                                                        question
                                                                      )}
                                                                      handleIncludeImageClick={() => {
                                                                        handleShowMediaClick(
                                                                          index,
                                                                          altIdx
                                                                        );
                                                                      }}
                                                                      isIncludeImageChecked={
                                                                        showMedia
                                                                      }
                                                                      onDeleteClick={() => {
                                                                        setDeleteQuestionPopupState(
                                                                          {
                                                                            sectionIndex:
                                                                              effectiveSectionIndex,
                                                                            subSectionIndex,
                                                                            questionIndex:
                                                                              index,
                                                                            altIdx:
                                                                              altIdx,
                                                                          }
                                                                        );
                                                                      }}
                                                                      handleReplaceClick={() =>
                                                                        handleReplaceClick(
                                                                          {
                                                                            sectionIndex:
                                                                              effectiveSectionIndex,
                                                                            subSectionIndex:
                                                                              subSectionIndex,
                                                                            index:
                                                                              index,
                                                                            altIdx:
                                                                              altIdx,
                                                                            currentQuestion:
                                                                              question,
                                                                          }
                                                                        )
                                                                      }
                                                                    />
                                                                  </Box>
                                                                ) : (
                                                                  // Published state
                                                                  <Box
                                                                    {...altDraggable.dragHandleProps}
                                                                  />
                                                                )
                                                              }
                                                              question={
                                                                question
                                                              }
                                                              showQuestionStats
                                                              showAnswer
                                                              imageResizeEnabled={
                                                                true
                                                              }
                                                              key={
                                                                'ques_' +
                                                                index +
                                                                '-' +
                                                                altIdx
                                                              }
                                                            />
                                                          </Box>
                                                        )}
                                                      </Draggable>
                                                      {altIdx <
                                                        arr.length - 1 &&
                                                        renderOrBox()}
                                                    </React.Fragment>
                                                  );
                                                }
                                              )}
                                              {altProvided.placeholder}
                                            </Box>
                                          );
                                        }}
                                      </Droppable>
                                    </Box>
                                  )}
                                </Box>
                              );
                            }}
                          </Draggable>
                        );
                      })}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            )
          )}
        </DragDropContext>
      ) : null}

      <ActionsPopup
        open={!!deleteQuestionPopupState}
        fontSmall={true}
        iconName="delete"
        popupText="Are you sure you want to delete this question"
        handleClose={() => setDeleteQuestionPopupState(undefined)}
        noClickHandler={() => setDeleteQuestionPopupState(undefined)}
        yesClickHandler={async () => {
          if (!deleteQuestionPopupState) {
            setDeleteQuestionPopupState(undefined);
            return;
          }
          const {
            sectionIndex: secIdx,
            subSectionIndex: subSecIdx,
            questionIndex: qIdx,
            altIdx: aIdx,
          } = deleteQuestionPopupState;
          if (typeof qIdx !== 'number') {
            setDeleteQuestionPopupState(undefined);
            return;
          }
          if (aIdx !== undefined) {
            deleteQuestion({
              subSectionIndex: 0,
              questionIndex: qIdx,
              altIdx: aIdx,
            });
          } else {
            deleteQuestion({
              subSectionIndex: 0,
              questionIndex: qIdx,
            });
          }
          dispatch(
            setToastInfo({
              label: 'Question deleted successfully',
              variant: 'info',
              open: true,
            })
          );
          setDeleteQuestionPopupState(undefined);
        }}
      />
    </Box>
  );
};

// =====================================================

const renderOrBox = () => {
  return (
    <Box sx={{ px: { xs: pxToRem(10), md: pxTovW(20) } }}>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: '#DDF0FF',
            color: '#000',
            padding: '10px',
            border: '1px solid #007CDC',
            borderRadius: '50%',
            zIndex: 1,
          }}
        >
          OR
        </Box>

        <Box
          sx={{
            height: '1px',
            width: '100%',
            backgroundColor: '#007CDC',
            position: 'absolute',
            top: '50%',
            left: '0',
            right: '0',
            transform: 'translateY(-50%)',
          }}
        />
      </Box>
    </Box>
  );
};

// =====================================================
